/**
 * Awards motion, from hung-hola blocks/awards/view.js (docs/animation/awards.md there): both the ring and the press
 * row run right to left, forever, at a constant speed (half the Figma prototype's, owner).
 * - Ring: the badge list turns counter-clockwise around the centre of the badge group's box (785.08, 701.66 in the
 *   1570×712 ring frame), 9°/s. The clip box and the bottom fade (.awards__ring) stay still.
 * - Press row: marquee. Every list moves left by one set width (list + gap) and gsap.utils.wrap keeps x inside
 *   [-setWidth, 0), so each copy takes the place of the one next to it; x = 0 is the static row. 85 px/s. Lists are
 *   cloned (aria-hidden) when two sets do not cover the track at x = -setWidth.
 * - Glow: opacity 0.6 → 0.3 and back, 2 s each way.
 * - Ring and press row slow down under the mouse and follow the page scroll (shared loopMotion()); the glow keeps
 *   its own pace.
 * All three start at the static Figma frame, pause off screen, and stay still with ?still or reduced motion.
 * Desktop fit (owner): above 1280px the block is as tall as the screen below the header, clamped between
 * MIN_HEIGHT and the Figma 905px, and the stage scales by height / 905 (--awards-scale). CSS cannot divide px by px,
 * so it is set here, also with ?still and reduced motion (it is size, not motion).
 * The heading's fade-in is the shared data-px-fade-in (assets/js/fade-in.ts).
 */
import { motionLib } from '../../assets/js/motion';
import { loopMotion } from '../../assets/js/loop-motion';

const RING_SPEED = 9; // deg per second, counter-clockwise
const RING_ORIGIN = '785.08px 701.66px';
const PRESS_SPEED = 85; // px per second, right to left
const GLOW_DURATION = 2; // s per leg
const GLOW_OPACITY = 0.3;
const FIGMA_HEIGHT = 905; // px, desktop frame
const MIN_HEIGHT = 720; // px, the block never gets shorter on desktop
const HEADER_HEIGHT = 81; // px, fallback when the header has not measured itself (--px-header-height)

type Tween = ReturnType<NonNullable<typeof window.gsap>['to']>;

function initAwards(wrapper: HTMLElement): void {
	const desktop = window.matchMedia('(width > 1280px)');
	const headerHeight = (): number =>
		parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--px-header-height')) || HEADER_HEIGHT;
	const fit = (): void => {
		const height = Math.min(FIGMA_HEIGHT, Math.max(MIN_HEIGHT, window.innerHeight - headerHeight()));
		wrapper.style.setProperty('--awards-scale', String(desktop.matches ? height / FIGMA_HEIGHT : 1));
	};
	fit();
	window.addEventListener('resize', fit);

	// The stage is scaled on desktop, so the block cannot take its height from it: pass its layout height (unscaled),
	// which grows past the Figma 905px when the heading copy is long
	const stage = wrapper.querySelector<HTMLElement>('[data-awards-stage]');
	if (stage) {
		new ResizeObserver(() => wrapper.style.setProperty('--awards-stage-height', `${stage.offsetHeight}px`)).observe(stage);
	}

	const ring = wrapper.querySelector<HTMLElement>('[data-awards-ring]');
	const track = wrapper.querySelector<HTMLElement>('[data-awards-track]');
	const lists = [...wrapper.querySelectorAll<HTMLElement>('[data-awards-list]')];
	const glow = wrapper.querySelector<HTMLElement>('[data-awards-glow]');
	const lib = motionLib();
	if (!ring || !glow || !lib) {
		return;
	}
	const { gsap } = lib;

	let ringTween: Tween | null = null;
	let glowTween: Tween | null = null;
	let pressTween: Tween | null = null;
	let inView = false;

	const buildPress = (): void => {
		if (!track || !lists.length) {
			return; // no press row ("Press row" off or no articles): the ring and the glow still move
		}
		const progress = pressTween ? pressTween.progress() : 0;
		pressTween?.kill();
		pressTween = null;
		gsap.set(lists, { clearProps: 'transform' });

		const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
		// Layout width, not getBoundingClientRect: the stage may be scaled, x is in the list's own pixels
		const setWidth = parseFloat(getComputedStyle(lists[0]).width) + gap;
		if (!setWidth) {
			return;
		}

		// At x = -setWidth (the worst point of the loop) the last list must still reach the right edge
		while ((lists.length - 1) * setWidth < track.clientWidth + gap) {
			const copy = lists[0].cloneNode(true) as HTMLElement;
			copy.setAttribute('aria-hidden', 'true');
			copy.querySelectorAll('img').forEach((img) => (img.alt = ''));
			track.append(copy);
			lists.push(copy);
		}

		const wrapX = gsap.utils.wrap(-setWidth, 0);
		gsap.set(lists, { x: wrapX(-progress * setWidth) });
		pressTween = gsap.to(lists, {
			x: `-=${setWidth}`,
			duration: setWidth / PRESS_SPEED,
			ease: 'none',
			repeat: -1,
			modifiers: { x: gsap.utils.unitize(wrapX) },
			paused: !inView,
		});
	};

	ringTween = gsap.to(ring, {
		rotation: -360,
		transformOrigin: RING_ORIGIN,
		duration: 360 / RING_SPEED,
		ease: 'none',
		repeat: -1,
		paused: true,
	});
	glowTween = gsap.to(glow, { opacity: GLOW_OPACITY, duration: GLOW_DURATION, ease: 'none', yoyo: true, repeat: -1, paused: true });
	buildPress();

	// Slower under the mouse, pushed by the page scroll (assets/js/loop-motion.ts)
	loopMotion()?.add({ hover: ring, apply: (scale) => ringTween?.timeScale(scale) });
	if (track) {
		loopMotion()?.add({ hover: track, apply: (scale) => pressTween?.timeScale(scale) });
	}

	// Rebuild the marquee when the track or the articles change size (breakpoint, logos loading)
	let frame = 0;
	const resizeObserver = new ResizeObserver(() => {
		cancelAnimationFrame(frame);
		frame = requestAnimationFrame(buildPress);
	});
	if (track && lists.length) {
		resizeObserver.observe(track);
		resizeObserver.observe(lists[0]);
	}

	new IntersectionObserver(([entry]) => {
		inView = entry.isIntersecting;
		[ringTween, glowTween, pressTween].forEach((tween) => (inView ? tween?.play() : tween?.pause()));
	}).observe(wrapper);
}

document.querySelectorAll<HTMLElement>('[data-px-block="awards"]').forEach(initAwards);
